/**
 * Jobqueuecontext.js
 * Global job queue for Legal Drafting + Brand Generator — survives page navigation.
 *
 * Job types:
 *   legal — polls GET /api/legal-drafting/drafts/:id
 *   brand — polls POST /api/brand-generator/status with request_id
 */

import React, {
  createContext, useContext, useState, useEffect,
  useCallback, useRef,
} from 'react';
import apiService from '../services/api';

const STORAGE_KEY = 'jobQueueJobs';

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveToStorage(jobs) {
  try {
    const toSave = jobs.filter(j => {
      if (j.status === 'generating' || j.status === 'pending') return true;
      if (j.status === 'done' || j.status === 'failed') {
        return (Date.now() - (j.completedAt || j.createdAt)) < 30 * 60 * 1000;
      }
      return false;
    });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch { }
}

const JobQueueContext = createContext(null);

export const useJobQueue = () => {
  const ctx = useContext(JobQueueContext);
  if (!ctx) throw new Error('useJobQueue must be used within JobQueueProvider');
  return ctx;
};

export const JobQueueProvider = ({ children, onJobCompleted, onQuotaUpdate }) => {
  const [jobs, setJobs] = useState(() => loadFromStorage());
  const pollRef      = useRef(null);
  const pollInFlight = useRef(new Set());

  useEffect(() => { saveToStorage(jobs); }, [jobs]);

  const addJob = useCallback((job) => {
    const jobType = job.jobType || 'legal';
    const uid = jobType === 'brand' ? `brand_${job.requestId}` : `legal_${job.draftId}`;
    const newJob = {
      uid, jobType,
      draftId: job.draftId || null,
      draftingType: job.draftingType || '',
      tmNo: job.tmNo || null,
      requestId: job.requestId || null,
      keywords: job.keywords || null,
      natureOfBusiness: job.natureOfBusiness || null,
      title: job.title || (jobType === 'brand' ? 'Brand Names' : 'Legal Document'),
      status: 'generating',
      createdAt: Date.now(),
      completedAt: null,
      errorMessage: null,
      names: null,
    };
    setJobs(prev => {
      if (prev.find(j => j.uid === uid)) return prev;
      return [newJob, ...prev];
    });
  }, []);

  const updateJob = useCallback((uid, patch) => {
    setJobs(prev => prev.map(j => j.uid === uid ? { ...j, ...patch } : j));
  }, []);

  const removeJob = useCallback((uid) => {
    setJobs(prev => prev.filter(j => j.uid !== uid));
  }, []);

  const cancelJob = useCallback(async (uid, { onAlreadyDone, onStillProcessing } = {}) => {
    const job = jobsRef.current.find(j => j.uid === uid);
    if (!job) return;
    if (job.jobType === 'legal' && job.draftId) {
      try {
        const res = await apiService.cancelLegalDraft(job.draftId);
        if (res?.openai_already_done) {
          updateJob(uid, { status: 'done', completedAt: Date.now() });
          onAlreadyDone?.();
          return { alreadyDone: true };
        }
        if (res?.openai_still_processing) {
          setJobs(prev => prev.filter(j => j.uid !== uid));
          onStillProcessing?.();
          return { stillProcessing: true };
        }
      } catch { }
    }
    setJobs(prev => prev.filter(j => j.uid !== uid));
    return { cancelled: true };
  }, [updateJob]);

  const clearCompleted = useCallback(() => {
    setJobs(prev => prev.filter(j => j.status === 'generating' || j.status === 'pending'));
  }, []);

  const jobsRef = useRef(jobs);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  const pollJobs = useCallback(async () => {
    const liveJobs = jobsRef.current.filter(j => {
      if (pollInFlight.current.has(j.uid)) return false;
      // Brand jobs: poll only while generating/pending. Stop when processing (page handles it).
      if (j.jobType === 'brand') return j.status === 'generating' || j.status === 'pending';
      // Legal jobs: poll while generating/pending
      return j.status === 'generating' || j.status === 'pending';
    });
    if (liveJobs.length === 0) return;

    await Promise.all(liveJobs.map(async (job) => {
      pollInFlight.current.add(job.uid);
      try {
        const current = jobsRef.current.find(j => j.uid === job.uid);
        if (!current || (current.status !== 'generating' && current.status !== 'pending')) return;

        if (job.jobType === 'brand') {
          const res = await apiService.checkBrandGeneratorStatus(job.requestId, {
            keywords: job.keywords,
            nature_of_business: job.natureOfBusiness,
          });
          if (!res?.success) return;
          if (res.status === 'completed') {
            // Mark as 'processing' — names came back from openai but TM search
            // + sort + slice still needs to run on the page. The page will call
            // updateJob({ status: 'done' }) after save-names succeeds.
            updateJob(job.uid, { status: 'processing', names: res.data });
          } else if (res.status === 'failed') {
            updateJob(job.uid, { status: 'failed', completedAt: Date.now(), errorMessage: res.message || 'Failed' });
            onJobCompleted?.(job.uid, 'failed', { jobType: 'brand' });
          }
        } else {
          const res = await apiService.getLegalDraft(job.draftId);
          if (!res?.success || !res?.data) return;
          const draft = res.data;
          if (draft.status === 'done') {
            updateJob(job.uid, { status: 'done', completedAt: Date.now() });
            onJobCompleted?.(job.uid, 'done', { jobType: 'legal' });
            onQuotaUpdate?.();
          } else if (draft.status === 'failed') {
            updateJob(job.uid, { status: 'failed', completedAt: Date.now(), errorMessage: draft.error_message || 'Generation failed' });
            onJobCompleted?.(job.uid, 'failed', { jobType: 'legal' });
          }
        }
      } catch { }
      finally { pollInFlight.current.delete(job.uid); }
    }));
  }, [updateJob, onJobCompleted, onQuotaUpdate]);

  useEffect(() => {
    pollRef.current = setInterval(pollJobs, 10000);
    return () => clearInterval(pollRef.current);
  }, [pollJobs]);

  const pendingCount   = jobs.filter(j => j.status === 'generating' || j.status === 'pending' || j.status === 'processing').length;
  const completedCount = jobs.filter(j => j.status === 'done').length;
  const failedCount    = jobs.filter(j => j.status === 'failed').length;
  const legalJobs      = jobs.filter(j => (j.jobType || 'legal') === 'legal');
  const brandJobs      = jobs.filter(j => j.jobType === 'brand');

  return (
    <JobQueueContext.Provider value={{
      jobs, legalJobs, brandJobs,
      addJob, updateJob, removeJob, cancelJob, clearCompleted,
      pendingCount, completedCount, failedCount,
    }}>
      {children}
    </JobQueueContext.Provider>
  );
};

export default JobQueueContext;